import { FormApi } from "final-form";
import { Form, useForm, useFormState } from "react-final-form";
import { Trans, useTranslation } from "react-i18next";
import { shallowEqual } from "react-redux";
import { useNavigate } from "react-router";
import { useSelector } from "src/store";

import { selectProvisionings } from "@dashboard/provisioning/store/selectors";
import { Provisioning } from "@dashboard/provisioning/types";
import { useGetCurrentWorkspaceFdroids } from "@dashboard/repositories/api/helpers";
import { Row, SectionDescription } from "@dashboard/shared/styles";
import { Typography, useTheme } from "@mui/material";
import { CustomAlert } from "@shared/CustomAlert/CustomAlert";
import { DangerOutlinedButton, PrimaryOutlinedButton } from "@shared/CustomButton";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";
import {
    MinimalTable,
    MinimalTableBody,
    MinimalTableCell,
    MinimalTableContainer,
    MinimalTableHeader,
    MinimalTableHeaderCell,
    MinimalTableRow,
} from "@shared/table/MinimalTableComponents";
import { TextLink } from "@shared/TextLink";
import { useQueryErrorToast } from "@shared/toasts/useQueryErrorToast";

import {
    CollapsibleProvider,
    CollapsibleSectionDetails,
    CollapsibleSectionSummary,
    CollapsibleSectionTitle,
    useCollapsiblePersistence,
} from "../../shared/components/CollapsibleSection";
import { requiredInternetConfigMessage } from "../utils";

import { hasInternetConfig } from "./InternetConfig/InternetConfig.utils";

type FormValues = { repositoryName: string };

export const PrivateRepositories = () => {
    const { value: initialValue, onChange } = useCollapsiblePersistence(
        "provisioningPage.collapsedSections",
        "PrivateRepositories",
    );
    const parentForm = useForm();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const provisionings = useSelector(selectProvisionings, shallowEqual);
    const { values: parentFormValues } = useFormState<Partial<Provisioning>>();
    const isDisabled = !hasInternetConfig(parentFormValues?.settings?.internetConfig);
    const repos = parentFormValues?.settings?.fDroidRepos || [];

    const fdroids = useGetCurrentWorkspaceFdroids();
    const { data: fdroidRepos = [] } = fdroids;
    useQueryErrorToast(fdroids);
    // Named so that adding a repository resets the picker to the same starting point the form opened with.
    // It used to reset to the values just submitted, which left the entry that was added still selected.
    const initialValues: FormValues = { repositoryName: fdroidRepos[0]?.name };

    const onSubmit = (values: FormValues, form: FormApi<FormValues>) => {
        const value = fdroidRepos.find((r) => r.name === values.repositoryName);
        if (repos.length) {
            parentForm.change("settings.fDroidRepos", [...repos, value]);
        }
        if (!repos.length) {
            parentForm.change("settings.fDroidRepos", [value]);
        }
        form.restart(initialValues);
    };
    const validate = (values: FormValues) => {
        const repoAdded = repos.some((r) => r.name === values.repositoryName);

        if (!values.repositoryName) {
            return { repositoryName: t("pages:provisioningPage.input.repositoryName.validation.notSelected") };
        }
        if (repoAdded) {
            return { repositoryName: t("pages:provisioningPage.input.repositoryName.validation.alreadyAdded") };
        }
    };
    const handleDelete = (repoId: number) => {
        parentForm.change(
            "settings.fDroidRepos",
            repos.filter((repo) => repo.id !== repoId),
        );
    };

    return (
        <Form<FormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={initialValues}
            render={({ form }) => (
                <CollapsibleProvider initialValue={initialValue} onChange={onChange} disabled={isDisabled}>
                    <CollapsibleSectionSummary>
                        <CollapsibleSectionTitle>
                            {t("pages:provisioningPage.section.privateRepositories.title")}
                        </CollapsibleSectionTitle>
                    </CollapsibleSectionSummary>
                    <CollapsibleSectionDetails error={isDisabled ? requiredInternetConfigMessage : undefined}>
                        <SectionDescription>
                            {t("pages:provisioningPage.section.privateRepositories.description")}
                        </SectionDescription>
                        <MinimalTableContainer>
                            <MinimalTable>
                                <MinimalTableHeader>
                                    <MinimalTableRow>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.url")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.title")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="left">
                                            <Typography fontWeight="bold">{t("common:label.description")}</Typography>
                                        </MinimalTableHeaderCell>
                                        <MinimalTableHeaderCell align="right"></MinimalTableHeaderCell>
                                    </MinimalTableRow>
                                </MinimalTableHeader>
                                <MinimalTableBody>
                                    {repos.length ? (
                                        repos.map((item) => (
                                            <MinimalTableRow key={item.id}>
                                                <MinimalTableCell component="th" scope="row" align="left">
                                                    <a
                                                        href={`${"https://apps.emteria.com/fdroid/"}${item.name}`}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                    >
                                                        {`${"https://apps.emteria.com/fdroid/"}${item.name}`}
                                                    </a>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.title}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="left">
                                                    <Typography>{item.description}</Typography>
                                                </MinimalTableCell>
                                                <MinimalTableCell align="right">
                                                    <DangerOutlinedButton
                                                        onClick={() => handleDelete(item.id)}
                                                        tooltipProps={{ title: t("common:button.delete") }}
                                                        name="private-repository-delete"
                                                    >
                                                        <i className="fa-solid fa-trash" />
                                                    </DangerOutlinedButton>
                                                </MinimalTableCell>
                                            </MinimalTableRow>
                                        ))
                                    ) : (
                                        <MinimalTableRow>
                                            <MinimalTableCell align="center" colSpan={4}>
                                                <Typography>{t("pages:provisioningPage.table.noPrivateRepositories")}</Typography>
                                            </MinimalTableCell>
                                        </MinimalTableRow>
                                    )}
                                </MinimalTableBody>
                            </MinimalTable>
                        </MinimalTableContainer>
                        {fdroidRepos.length ? (
                            <Row style={{ marginTop: theme.spacing(4) }}>
                                <ControlledSelectField
                                    name="repositoryName"
                                    label={t("pages:provisioningPage.input.repositoryName.caption")}
                                    disabled={!provisionings.length}
                                    options={fdroidRepos.map((item) => ({
                                        label: item.name,
                                        value: item.name,
                                    }))}
                                    fullWidth
                                />
                                <PrimaryOutlinedButton
                                    onClick={form.submit}
                                    disabled={!provisionings.length}
                                    name="private-repository-add"
                                >
                                    {t("common:button.add")}
                                </PrimaryOutlinedButton>
                            </Row>
                        ) : (
                            <CustomAlert style={{ marginTop: theme.spacing(4) }} severity="info">
                                <Trans
                                    ns="pages"
                                    i18nKey="provisioningPage.alert.createPrivateRepository"
                                    components={{
                                        // Deliberately not named "link": html-parse-stringify treats
                                        // <link> as a void element, so Trans would drop the label out
                                        // of the anchor and leave it unclickable.
                                        repositoriesLink: (
                                            <TextLink
                                                component="button"
                                                type="button"
                                                sx={{ marginLeft: 1, marginRight: 1, paddingBottom: 1 }}
                                                onClick={() => void navigate("/dashboard/repositories")}
                                                tooltip={t("pages:provisioningPage.tooltip.goToRepositoriesPage")}
                                            />
                                        ),
                                    }}
                                />
                            </CustomAlert>
                        )}
                    </CollapsibleSectionDetails>
                </CollapsibleProvider>
            )}
        />
    );
};
